/* eslint-disable no-control-regex */
/**
 * ANSI Escape Code Parser
 * Parses ANSI escape codes and converts them to styled React elements
 */

export type AnsiStyle = {
  color?: string;
  backgroundColor?: string;
  fontWeight?: 'bold' | 'normal';
  fontStyle?: 'italic' | 'normal';
  textDecoration?: 'underline' | 'line-through' | 'none';
  opacity?: number;
};

export type AnsiSegment = {
  text: string;
  style: AnsiStyle;
};

// Standard ANSI color palette
const ANSI_COLORS: Record<number, string> = {
  30: '#1e293b', // black
  31: '#ef4444', // red
  32: '#22c55e', // green
  33: '#eab308', // yellow
  34: '#3b82f6', // blue
  35: '#a855f7', // magenta
  36: '#22d3ee', // cyan
  37: '#f1f5f9', // white
  // Bright colors
  90: '#64748b', // bright black (gray)
  91: '#f87171', // bright red
  92: '#4ade80', // bright green
  93: '#facc15', // bright yellow
  94: '#60a5fa', // bright blue
  95: '#c084fc', // bright magenta
  96: '#67e8f9', // bright cyan
  97: '#ffffff', // bright white
};

// Background colors
const ANSI_BG_COLORS: Record<number, string> = {
  40: '#1e293b', // black
  41: '#ef4444', // red
  42: '#22c55e', // green
  43: '#eab308', // yellow
  44: '#3b82f6', // blue
  45: '#a855f7', // magenta
  46: '#22d3ee', // cyan
  47: '#f1f5f9', // white
  // Bright backgrounds
  100: '#64748b', // bright black
  101: '#f87171', // bright red
  102: '#4ade80', // bright green
  103: '#facc15', // bright yellow
  104: '#60a5fa', // bright blue
  105: '#c084fc', // bright magenta
  106: '#67e8f9', // bright cyan
  107: '#ffffff', // bright white
};

// Parse SGR (Select Graphic Rendition) parameters
function applySgrCode(code: number, style: AnsiStyle): AnsiStyle {
  const newStyle = { ...style };

  if (code === 0) {
    // Reset all attributes
    return {};
  } else if (code === 1) {
    newStyle.fontWeight = 'bold';
  } else if (code === 2) {
    newStyle.opacity = 0.5; // Dim
  } else if (code === 3) {
    newStyle.fontStyle = 'italic';
  } else if (code === 4) {
    newStyle.textDecoration = 'underline';
  } else if (code === 9) {
    newStyle.textDecoration = 'line-through';
  } else if (code === 22) {
    newStyle.fontWeight = 'normal';
    newStyle.opacity = undefined;
  } else if (code === 23) {
    newStyle.fontStyle = 'normal';
  } else if (code === 24 || code === 29) {
    newStyle.textDecoration = 'none';
  } else if ((code >= 30 && code <= 37) || (code >= 90 && code <= 97)) {
    newStyle.color = ANSI_COLORS[code];
  } else if (code === 39) {
    // Default foreground color
    newStyle.color = undefined;
  } else if ((code >= 40 && code <= 47) || (code >= 100 && code <= 107)) {
    newStyle.backgroundColor = ANSI_BG_COLORS[code];
  } else if (code === 49) {
    // Default background color
    newStyle.backgroundColor = undefined;
  }

  return newStyle;
}

// ANSI escape sequence regex
// Matches: ESC[<params>m where ESC can be \x1b, \033, \e
const ANSI_REGEX = /\x1b\[([0-9;]*)m/g;

/**
 * Parse a string containing ANSI escape codes into segments
 */
export function parseAnsi(text: string): AnsiSegment[] {
  const segments: AnsiSegment[] = [];
  let currentStyle: AnsiStyle = {};
  let lastIndex = 0;
  let match;

  // Reset regex state
  ANSI_REGEX.lastIndex = 0;

  while ((match = ANSI_REGEX.exec(text)) !== null) {
    // Add text before this escape sequence
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index);
      if (textContent) {
        segments.push({
          text: textContent,
          style: { ...currentStyle },
        });
      }
    }

    // Parse the SGR codes
    const codes = match[1] ? match[1].split(';').map((c) => parseInt(c, 10) || 0) : [0];

    // Apply each code
    for (const code of codes) {
      currentStyle = applySgrCode(code, currentStyle);
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    const textContent = text.slice(lastIndex);
    if (textContent) {
      segments.push({
        text: textContent,
        style: { ...currentStyle },
      });
    }
  }

  return segments.length > 0 ? segments : [{ text, style: {} }];
}

/**
 * Convert AnsiStyle to React CSSProperties
 */
export function ansiStyleToCss(style: AnsiStyle): React.CSSProperties {
  const css: React.CSSProperties = {};

  if (style.color) css.color = style.color;
  if (style.backgroundColor) css.backgroundColor = style.backgroundColor;
  if (style.fontWeight) css.fontWeight = style.fontWeight;
  if (style.fontStyle) css.fontStyle = style.fontStyle;
  if (style.textDecoration) css.textDecoration = style.textDecoration;
  if (style.opacity !== undefined) css.opacity = style.opacity;

  return css;
}

/**
 * Check if text contains ANSI escape codes
 */
export function hasAnsiCodes(text: string): boolean {
  return /\x1b\[[0-9;]*m/.test(text);
}

/**
 * Strip ANSI codes from text
 */
export function stripAnsi(text: string): string {
  return text.replace(/\x1b\[[0-9;]*m/g, '');
}
